import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useStableValue } from '@shared/util/hooks';
import { JsonStorage } from './JsonStorage';
import { parseStoredJson } from './parseStoredJson';

/**
 * useLocalStorage
 *
 * Drop-in replacement for useState with localstorage backing.
 *
 * @param key the storage key associated with the data
 * @param defaultValue fallback value to return if stored value doesn't exist
 * @param timeToLiveMs the time for the value to be valid in the storage.
 * Defaults to undefined which means the value will never expire.
 * @returns tuple of [value, setValue, loading, removeValue]
 *
 * Usage notes:
 * - The value is always stored as JSON, which allows for storing arrays,
 *   object, booleans, strings, or numbers.
 * - The value type can be inferred from the default value, or can be specified
 *   by calling useLocalStorage<SometType>(key, defaultValue, ttl);
 * - If value is not found in storage, or is expired, the default value will be
 *   returned.
 * - The defaultValue will be returned immediately and then will be replaced
 *   with the stored value, if found and not expired. If you want to wait until
 *   the stored value is retrieved before rendering your component, use the
 *   loading state returned from the hook.
 * - If the defaultValue is non-primitive (e.g. an empty object or array), it
 *   should be defined as a constant outside the component, or memoized, to
 *   avoid unnecessary state changes.
 * - If you want an undefined value to be returned when the value is not found
 *   or expired, the storage type must be specified as a union with undefined,
 *   and the defaultValue should be passed as undefined:
 *
 * const [value, setValue] = useLocalStorage<T | undefined>(key, undefined);
 *
 * Updating the value:
 * - the updater accepts a SetStateAction, same as useState.
 *
 * - pass a new value directly
 * - setValue(newValue);
 *
 * - pass a function which accepts the previous value and returns the new value
 * - setValue(prev => ({ ...prev, ...changes });
 */

type UseLocalStorageHook<T> = [T, Dispatch<SetStateAction<T>>, boolean, VoidFunction];

const erroredKeys = new Set<string>();

export function useLocalStorage<T>(
  key: string,
  _defaultValue: T,
  ttl?: number,
): UseLocalStorageHook<T> {
  // manage default value in state, so identity changes for passed objects or
  // arrays don't trigger unnecessary state changes
  const defaultValue = useDefaultValue(_defaultValue);
  const [value, setValue] = useStableValue<T>(defaultValue);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (
      import.meta.env.DEV &&
      defaultValue !== _defaultValue &&
      isEqual(defaultValue, _defaultValue) &&
      !erroredKeys.has(key)
    ) {
      console.warn('useLocalStorage: defaultValue changed ID across renders', key, _defaultValue);
      erroredKeys.add(key);
    }
  }, [key, defaultValue, _defaultValue]);

  useEffect(() => {
    // when key or defaultValue changes, fetch from storage
    JsonStorage.get<T>(key).then(storedValue => {
      setValue(storedValue ?? defaultValue);
      setLoading(false);
    });
  }, [key, defaultValue, setValue]);

  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (event.key === key) {
        const storedValue = parseStoredJson<T>(event.newValue);
        setValue(storedValue ?? defaultValue);
      }
    };
    window.addEventListener('storage', listener);
    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue, setValue]);

  const saveValue = useCallback(
    (update: SetStateAction<T>) => {
      setValue(prev => {
        const updatedValue = update instanceof Function ? update(prev) : update;
        if (!isEqual(prev, updatedValue)) {
          JsonStorage.set(key, updatedValue, ttl);
          return updatedValue;
        } else {
          return prev;
        }
      });
    },
    [key, ttl, setValue],
  );

  const removeValue = useCallback(() => {
    JsonStorage.remove(key);
    setValue(defaultValue);
  }, [key, defaultValue, setValue]);

  return [value, saveValue, loading, removeValue];
}

function useDefaultValue<T>(_defaultValue: T) {
  const [defaultValue, setDefaultValue] = useStableValue<T>(_defaultValue);

  useEffect(() => {
    setDefaultValue(_defaultValue);
  }, [setDefaultValue, _defaultValue]);

  return defaultValue;
}
